import { Box, Flex, VStack, Text, Center } from "@chakra-ui/react";
import { useMemo } from "react";
import { useLunaMessages, useLunaIsThinking, useLunaChatIsClosed } from "state/luna";
import { members } from "state/members/member";
import { LunaMark } from "components/ds/LunaMark";
import { LunaErrorBanner } from "./LunaErrorBanner";
import { LunaMessageThread } from "./LunaMessageThread";
import { LunaMessageInput } from "./LunaMessageInput";

interface LunaChatViewProps {
  sessionId: string;
}

export const LunaChatView = ({ sessionId }: LunaChatViewProps) => {
  const messages = useLunaMessages(sessionId);
  const isThinking = useLunaIsThinking(sessionId);
  const isClosed = useLunaChatIsClosed(sessionId);
  const me = members.useGetMe();

  // Memoize greeting text - only compute once on mount
  const greetingText = useMemo(() => {
    const hour = new Date().getHours();
    let greeting: string;
    if (hour < 12) greeting = "Good Morning";
    else if (hour < 18) greeting = "Good Afternoon";
    else greeting = "Good Evening";

    const name = me?.firstName || "there";
    return `${greeting}, ${name}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only compute on mount

  const showEmptyState = messages.length === 0 && !isThinking;

  return (
    <Flex direction="column" h="full" w="full" bg="bg.page">
      {/* Error banner */}
      <LunaErrorBanner />

      {/* Message thread or empty state */}
      <Box flex={1} position="relative" minH={0}>
        {showEmptyState ? (
          <Center h="full">
            <VStack spacing="lg" textAlign="center">
              <LunaMark size={40} loadIn />
              <Box>
                <Text
                  fontFamily="heading"
                  fontSize="xl"
                  color="text.body.primary"
                  lineHeight={1.5}
                >
                  {greetingText}
                </Text>
                <Text
                  fontFamily="heading"
                  fontSize="xl"
                  color="text.body.tertiary"
                  lineHeight={1.5}
                >
                  How can I help you today?
                </Text>
              </Box>
            </VStack>
          </Center>
        ) : (
          <>
            <LunaMessageThread sessionId={sessionId} px="3xl" pt="3xl" />

            {/* Gradient fade */}
            <Box
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              height="100px"
              background="linear-gradient(to bottom, transparent, var(--chakra-colors-bg-page))"
              pointerEvents="none"
            />
          </>
        )}
      </Box>

      {/* Input area - fixed at bottom */}
      <Box
        flexShrink={0}
        bg="bg.page"
        pt="lg"
        pb="xl"
        px="3xl"
        position="relative"
        zIndex={20}
        overflow="visible"
      >
        {isClosed ? (
          <Box
            maxW="578px"
            mx="auto"
            w="100%"
            bg="linear-gradient(180deg, #D1DCE0, #D5D8FF)"
            borderRadius="container.sm"
            p="1px"
          >
            <Box
              bg="white"
              borderRadius="container.sm"
              px="xl"
              py="lg"
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
            >
              <LunaMark size={24} animated={false} />
              <Text
                fontSize="sm"
                fontWeight="500"
                textAlign="center"
                lineHeight="1.5"
                sx={{
                  background: "linear-gradient(119deg, var(--chakra-colors-luna-0) 5%, var(--chakra-colors-luna-100) 101%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Thanks for taking the time. This conversation is now closed and
                Luna is unable to receive any further replies to this thread.
                Have an awesome day!
              </Text>
            </Box>
          </Box>
        ) : (
          <LunaMessageInput sessionId={sessionId} />
        )}
      </Box>
    </Flex>
  );
};
